import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import store from "../store";
import { clearUserData } from "../store/userSlice";
import {
  refreshSessionData,
  registerSessionCryptoKey,
} from "../utils/refreshSessionData";
import { setSessionSyncInProgress } from "../utils/sessionSyncGuard";
import {
  getHomePathForRole,
  getRedirectForRole,
  isSameSession,
  parseSessionSyncPayload,
  SESSION_SYNC_KEY,
} from "../utils/sessionSync";

const SYNC_DEBOUNCE_MS = 500;

function redirectForSessionChange(pathname, previousUser, refreshedUser) {
  // If the current path doesn't suit the new role, force the role area.
  const roleRedirect = getRedirectForRole(pathname, refreshedUser.user_role);
  if (roleRedirect) {
    return roleRedirect;
  }

  // Same role area but different user — send to their home page so layouts
  // reload cleanly with the new user's data.
  const userChanged =
    !previousUser ||
    String(previousUser._id) !== String(refreshedUser._id) ||
    Number(previousUser.user_role) !== Number(refreshedUser.user_role);

  if (userChanged) {
    return getHomePathForRole(refreshedUser.user_role);
  }

  return null;
}

async function loadSessionAfterExternalLogin(dispatch) {
  await registerSessionCryptoKey();
  let refreshedUser = await refreshSessionData(dispatch);
  if (!refreshedUser) {
    await registerSessionCryptoKey();
    refreshedUser = await refreshSessionData(dispatch);
  }
  return refreshedUser;
}

export default function useSessionSync() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const syncTimerRef = useRef(null);
  const syncInFlightRef = useRef(false);
  const lastProcessedTsRef = useRef(null);
  const pathnameRef = useRef(location.pathname);

  pathnameRef.current = location.pathname;

  useEffect(() => {
    const runSync = async (payload) => {
      if (payload?.ts && payload.ts === lastProcessedTsRef.current) {
        return;
      }
      if (payload?.ts) {
        lastProcessedTsRef.current = payload.ts;
      }
      if (!store.getState().userStore.initialized) {
        return;
      }
      if (syncInFlightRef.current) {
        return;
      }

      syncInFlightRef.current = true;
      setSessionSyncInProgress(true);

      try {
        // ── Logout broadcast ──────────────────────────────────────────────
        if (payload?.logout) {
          dispatch(clearUserData());
          if (pathnameRef.current !== "/") {
            navigate("/", { replace: true });
          }
          return;
        }

        // ── Same session — nothing to do ─────────────────────────────────
        const previousUser = store.getState().userStore.data;
        if (
          payload &&
          !payload.logout &&
          isSameSession(previousUser, payload.userId, payload.userRole)
        ) {
          return;
        }

        // ── Load the new session's user data ─────────────────────────────
        let refreshedUser = null;
        try {
          refreshedUser = await loadSessionAfterExternalLogin(dispatch);
        } catch (err) {
          const status = err?.response?.status;
          if (status !== 401 && status !== 403) throw err;
          try {
            refreshedUser = await loadSessionAfterExternalLogin(dispatch);
          } catch {
            refreshedUser = null;
          }
        }

        if (!refreshedUser) {
          // Fall back to the payload's role to pick a safe landing page.
          if (payload?.userRole != null && pathnameRef.current !== "/") {
            navigate(getHomePathForRole(payload.userRole), { replace: true });
          }
          return;
        }

        const sessionChanged =
          !previousUser ||
          String(previousUser._id) !== String(refreshedUser._id) ||
          Number(previousUser.user_role) !== Number(refreshedUser.user_role);

        if (!sessionChanged) {
          return;
        }

        // Redux already has the new user's data — navigate with React Router
        // so the new page renders immediately without a blank reload.
        const target = redirectForSessionChange(
          pathnameRef.current,
          previousUser,
          refreshedUser
        );
        if (target && target !== pathnameRef.current) {
          navigate(target, { replace: true });
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          // Genuine auth failure — fall back using payload role if available.
          if (payload?.userRole != null && pathnameRef.current !== "/") {
            navigate(getHomePathForRole(payload.userRole), { replace: true });
            return;
          }
          dispatch(clearUserData());
          if (pathnameRef.current !== "/") {
            navigate("/", { replace: true });
          }
        }
      } finally {
        syncInFlightRef.current = false;
        setSessionSyncInProgress(false);
      }
    };

    const scheduleSync = (payload) => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
      syncTimerRef.current = setTimeout(() => runSync(payload), SYNC_DEBOUNCE_MS);
    };

    const onStorage = (event) => {
      if (event.key !== SESSION_SYNC_KEY) return;
      scheduleSync(parseSessionSyncPayload(event.newValue));
    };

    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
      setSessionSyncInProgress(false);
    };
  }, [dispatch, navigate]);
}
